import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilm, faImage, faMusic, faSearch, faBrain } from '@fortawesome/free-solid-svg-icons';

import { useContentData } from '../../hooks/useContentData';
import { getAIRecommendations, AIProvider } from '../../apis/aiService';
import { publicUrlStr } from '../../utils';
import * as S from './style';

interface SearchableItem {
  title: string;
  source: string;
  type: 'video' | 'image' | 'audio';
  category: string;
  yt?: boolean;
}

function textSearch(items: SearchableItem[], query: string): SearchableItem[] {
  const lower = query.toLowerCase();
  return items.filter(item =>
    item.title.toLowerCase().includes(lower) ||
    item.category.toLowerCase().includes(lower) ||
    item.type.includes(lower)
  );
}

interface AISearchProps {
  isOpen: boolean;
  onClose: () => void;
}

function AISearch({ isOpen, onClose }: AISearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchableItem[]>([]);
  const [aiSummary, setAiSummary] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchMode, setSearchMode] = useState<'text' | 'ai'>('text');
  const navigate = useNavigate();
  const { allMediaFlat } = useContentData();

  const allMedia: SearchableItem[] = allMediaFlat;

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    setIsSearching(true);
    setAiSummary('');

    const textResults = textSearch(allMedia, query);
    setResults(textResults);

    if (searchMode === 'ai') {
      try {
        const aiResult = await getAIRecommendations(
          allMedia.map(m => ({ ...m })),
          query,
          'ollama' as AIProvider
        );
        setAiSummary(aiResult);

        try {
          const parsed = JSON.parse(aiResult);
          if (Array.isArray(parsed)) {
            const aiItems = parsed
              .filter((r: any) => r.index >= 0 && r.index < allMedia.length)
              .map((r: any) => allMedia[r.index]);
            if (aiItems.length > 0) {
              setResults(aiItems);
            }
          }
        } catch {
          if (textResults.length === 0) {
            setResults(allMedia.slice(0, 5));
          }
        }
      } catch (err) {
        setAiSummary('AI 검색 중 오류가 발생했습니다. 텍스트 검색 결과를 표시합니다.');
      }
    }

    setIsSearching(false);
  };

  const handleItemClick = (item: SearchableItem) => {
    const mainUrl = publicUrlStr();
    switch (item.type) {
      case 'video':
        navigate(`${mainUrl}/video`);
        break;
      case 'image':
        navigate(`${mainUrl}/image`);
        break;
      case 'audio':
        navigate(`${mainUrl}/audio`);
        break;
    }
    onClose();
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'video': return faFilm;
      case 'image': return faImage;
      case 'audio': return faMusic;
      default: return faSearch;
    }
  };

  const getThumbUrl = (item: SearchableItem) => {
    if (item.type === 'video' && item.yt) {
      return `https://img.youtube.com/vi/${item.source}/default.jpg`;
    }
    if (item.type === 'video') {
      return `${publicUrlStr()}/videos/thumbnails/${item.source}.jpg`;
    }
    if (item.type === 'image') {
      return `${publicUrlStr()}/images/${item.source}`;
    }
    return undefined;
  };

  if (!isOpen) return null;

  return (
    <S.Overlay onClick={onClose}>
      <S.SearchModal onClick={(e) => e.stopPropagation()}>
        <S.SearchHeader>
          <S.SearchTitle>
            <FontAwesomeIcon icon={faBrain} style={{ color: '#33ff99' }} />
            GDFLIX 통합 검색
          </S.SearchTitle>
          <S.ModeToggle>
            <S.ModeBtn $active={searchMode === 'text'} onClick={() => setSearchMode('text')}>
              <FontAwesomeIcon icon={faSearch} /> 텍스트
            </S.ModeBtn>
            <S.ModeBtn $active={searchMode === 'ai'} onClick={() => setSearchMode('ai')}>
              <FontAwesomeIcon icon={faBrain} /> AI 검색
            </S.ModeBtn>
          </S.ModeToggle>
        </S.SearchHeader>

        <S.SearchForm onSubmit={handleSearch}>
          <S.SearchInput
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (searchMode === 'text' && e.target.value.trim()) {
                setResults(textSearch(allMedia, e.target.value));
              }
            }}
            placeholder={searchMode === 'ai' ? 'AI에게 콘텐츠를 추천받으세요...' : '콘텐츠 검색...'}
            autoFocus
          />
          <S.SearchBtn type="submit" disabled={isSearching}>
            {isSearching ? '검색 중...' : '검색'}
          </S.SearchBtn>
        </S.SearchForm>

        {aiSummary && (
          <S.AISummary>
            <FontAwesomeIcon icon={faBrain} /> {aiSummary}
          </S.AISummary>
        )}

        <S.ResultsList>
          {results.length > 0 ? (
            results.map((item, i) => (
              <S.ResultItem key={`${item.type}-${item.source}-${i}`} onClick={() => handleItemClick(item)}>
                <S.ResultThumb $url={getThumbUrl(item)}>
                  {!getThumbUrl(item) && <FontAwesomeIcon icon={getTypeIcon(item.type)} />}
                </S.ResultThumb>
                <S.ResultInfo>
                  <S.ResultTitle>{item.title}</S.ResultTitle>
                  <S.ResultMeta>
                    <S.TypeBadge $type={item.type}>
                      <FontAwesomeIcon icon={getTypeIcon(item.type)} /> {item.type}
                    </S.TypeBadge>
                    <span>{item.category}</span>
                  </S.ResultMeta>
                </S.ResultInfo>
              </S.ResultItem>
            ))
          ) : query.trim() ? (
            <S.NoResults>검색 결과가 없습니다</S.NoResults>
          ) : (
            <S.NoResults>검색어를 입력하세요</S.NoResults>
          )}
        </S.ResultsList>
      </S.SearchModal>
    </S.Overlay>
  );
}

export default AISearch;
